import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DarkModeService {
  private isDarkMode = signal<boolean>(false);
  readonly darkMode = this.isDarkMode.asReadonly();

  constructor() {
    const savedPreference = this.readPreference();
    if (savedPreference !== null) {
      this.isDarkMode.set(savedPreference === 'true');
    } else {
      const prefersDarkMode = this.getPrefersDarkMode();
      this.isDarkMode.set(prefersDarkMode);
    }

    this.listenForSystemPreferenceChanges((event) => {
      const systemPreference = event.matches;
      if (!this.readPreference()) {
        this.isDarkMode.set(systemPreference);
      }
    });

    this.applyDarkMode();
  }

  toggleDarkMode() {
    this.isDarkMode.update((current) => {
      const newMode = !current;
      this.writePreference(newMode);
      return newMode;
    });
    this.applyDarkMode();
  }

  private applyDarkMode() {
    if (this.isDarkMode()) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  private readPreference(): string | null {
    try {
      return localStorage.getItem('darkMode');
    } catch {
      return null;
    }
  }

  private writePreference(value: boolean): void {
    try {
      localStorage.setItem('darkMode', String(value));
    } catch {
      // Storage can be unavailable in some iOS standalone/browser contexts.
    }
  }

  private getPrefersDarkMode(): boolean {
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch {
      return false;
    }
  }

  private listenForSystemPreferenceChanges(
    listener: (event: MediaQueryListEvent) => void,
  ): void {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', listener);
        return;
      }

      if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(listener);
      }
    } catch {
      // Dark mode should never prevent the app from booting.
    }
  }
}
